import {
  RESOURCE_COVER_MAX_CHARS,
  RESOURCE_COVER_MAX_SIDE,
  RESOURCE_FILE_MAX_BYTES,
  RESOURCE_KIND_MIME_TYPES,
  RESOURCE_ROUTES,
  resourceResponseSchema,
  type Resource,
  type ResourceFileInput,
  type ResourceKind,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { fileToBase64 } from "@/features/kyc/kyc.api";

/**
 * Lo que el panel de socio y el de admin comparten sobre los recursos:
 * pedir el ARCHIVO de uno y guardarlo en el disco de quien hizo clic.
 *
 * `RESOURCE_ROUTES` ya trae el prefijo `/api`, de ahí el `baseUrl: ""`.
 */
const NO_PREFIX = { baseUrl: "" } as const;

/**
 * El recurso CON su archivo dentro (`file.dataUrl`). Se pide UNO, en el
 * clic: la lista de la biblioteca viaja sin bytes a propósito.
 */
export async function getResourceFile(id: string): Promise<Resource> {
  const data = await authFetch(RESOURCE_ROUTES.file(id), resourceResponseSchema, NO_PREFIX);
  return data.resource;
}

/**
 * Guarda un `data:` en el disco. Un ancla con `download` y no una
 * navegación: el navegador bloquea navegar a `data:`, pero descargarlo
 * desde un ancla sigue siendo un caso legítimo y no necesita blob ni
 * `createObjectURL` que luego haya que revocar.
 */
export function saveDataUrl(dataUrl: string, fileName: string): void {
  const anchor = document.createElement("a");
  anchor.href = dataUrl;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
}

/** Pide el archivo de un recurso y lo baja. */
export async function downloadResource(id: string): Promise<void> {
  const resource = await getResourceFile(id);
  if (!resource.file?.dataUrl) throw new Error("Ese recurso no trae archivo");
  saveDataUrl(resource.file.dataUrl, resource.file.fileName);
}

/**
 * De `File` a lo que viaja en el JSON, comprobando ANTES lo que el
 * contrato exigiría después: que el formato corresponda al tipo elegido
 * y que quepa. Rebotar aquí ahorra subir 8 MB para que el servidor diga
 * que no.
 */
export async function readResourceFile(
  file: File,
  kind: ResourceKind,
): Promise<ResourceFileInput> {
  const allowed: readonly string[] = RESOURCE_KIND_MIME_TYPES[kind];
  if (!allowed.includes(file.type)) {
    throw new Error(
      kind === "pdf" ? "Ese archivo no es un PDF." : "Solo imágenes: JPG, PNG o WebP.",
    );
  }
  if (file.size > RESOURCE_FILE_MAX_BYTES) {
    throw new Error("El archivo no puede pasar de 8 MB.");
  }
  return {
    fileName: file.name,
    mimeType: file.type as ResourceFileInput["mimeType"],
    dataBase64: await fileToBase64(file),
  };
}

/**
 * Lee la PORTADA y la deja en miniatura antes de subirla.
 *
 * La portada viaja en cada fila de la lista de la biblioteca, así que se
 * reduce en el navegador: una captura de pantalla de 5 MB multiplicada
 * por la rejilla es una pantalla que no carga. Si el navegador no puede
 * dibujar en un canvas (o el formato no se puede exportar), se usa el
 * archivo tal cual y el techo del contrato es el que decide — un aviso
 * claro vale más que una portada rota.
 */
export async function readResourceCover(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new Error("La portada tiene que ser una imagen.");
  }
  const original = `data:${file.type};base64,${await fileToBase64(file)}`;
  const reducida = await shrinkImage(original).catch(() => null);
  const cover = reducida ?? original;
  if (cover.length > RESOURCE_COVER_MAX_CHARS) {
    throw new Error("Esa portada pesa demasiado: usa una imagen más chica.");
  }
  return cover;
}

/** Redibuja la imagen con el lado mayor acotado. `null` si no se pudo. */
async function shrinkImage(dataUrl: string): Promise<string | null> {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  // Se comprueba ANTES de cargar la imagen: donde no hay canvas (jsdom,
  // un navegador con el lienzo bloqueado) esperar un `onload` que nunca
  // llega deja la subida colgada para siempre.
  if (!context) return null;

  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const element = new Image();
    element.onload = () => resolve(element);
    element.onerror = () => reject(new Error("No se pudo leer la imagen"));
    element.src = dataUrl;
  });

  const escala = Math.min(
    1,
    RESOURCE_COVER_MAX_SIDE / Math.max(image.naturalWidth, image.naturalHeight),
  );
  canvas.width = Math.round(image.naturalWidth * escala);
  canvas.height = Math.round(image.naturalHeight * escala);
  if (canvas.width === 0) return null;
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  const reducida = canvas.toDataURL("image/webp", 0.75);
  // Un canvas que no sabe exportar webp devuelve un PNG: si eso pasa y
  // sale más pesado que el original, el original gana.
  return reducida.startsWith("data:image/") && reducida.length < dataUrl.length
    ? reducida
    : null;
}

/** El peso de un archivo, en lo que una persona lee. */
export function formatFileSize(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
